/**
 * P16's two endpoints, wrapped over `useSession().request()` — **the one authenticated door**
 * (`web/src/auth/context.ts`).
 *
 * It sits beside `screens/shared/api.ts` rather than inside it for the reason
 * `screens/devices/api.ts` and `screens/copyMembers/api.ts` both give: that file is **P7's**, and
 * this PRD extends P7's screens without taking their files. Same division of labour as its
 * neighbour — this module shapes a path and reads a response, attaches no bearer or binding, and
 * decides no authorization.
 *
 * **Every reader here is defensive rather than a schema validator**, matching the house rule: a
 * field of the wrong shape is dropped, never thrown, because a malformed response should degrade
 * a screen rather than crash it. The one exception is the credential itself — a `BleCredential`
 * with no password is not a degraded credential, it is not a credential, so that read returns
 * failure rather than a half-built object.
 */

import { ROUTES, buildPath } from '@selogate/core'
import { field, stringField } from '../auth/transport'
import type { ApiRequestFn, ApiResult } from '../screens/shared/api'

/**
 * What `GET /gates/{id}/ble-access` returns.
 *
 * **This type is why `credentials.ts` exists.** It is the only shape in the client that carries a
 * gate password, and `device-cache.md` requires it to live in memory and nowhere else — so it is
 * deliberately not part of any snapshot, view model, or context value, and nothing constructs one
 * except the reader below.
 */
export interface BleCredential {
  readonly gateId: string
  /** The controller's advertised BLE name — what a scan filters on. */
  readonly bleName: string
  /** Sent to the controller as `PSW?<password>`. Never persisted, never logged (D85). */
  readonly blePassword: string
  /** The server's short-lived reuse hint (ISO). Governs re-fetching **while online** only. */
  readonly expiresAt: string
  /** Vendor GATT overrides, absent on stock firmware (D46). Protocol detail, not secrets. */
  readonly bleServiceUuid?: string
  readonly bleCharUuid?: string
  readonly bleSerial?: string
}

function ok<T>(status: number, data: T): ApiResult<T> {
  return { ok: true, status, data }
}

function fail<T>(response: { readonly status: number; readonly body: unknown }): ApiResult<T> {
  const code = stringField(response.body, 'code')
  const message = stringField(response.body, 'message')
  return {
    ok: false,
    status: response.status,
    ...(code === undefined ? {} : { code }),
    ...(message === undefined ? {} : { message }),
  }
}

const isSuccess = (status: number): boolean => status >= 200 && status < 300

export async function fetchBleAccess(
  request: ApiRequestFn,
  gateId: string,
): Promise<ApiResult<BleCredential>> {
  const response = await request({
    method: ROUTES.gateBleAccess.method,
    path: buildPath(ROUTES.gateBleAccess, { gateId }),
  })
  if (!isSuccess(response.status)) return fail(response)

  const bleName = stringField(response.body, 'bleName')
  const blePassword = stringField(response.body, 'blePassword')
  const expiresAt = stringField(response.body, 'expiresAt')
  // A response missing any of the three is not a degraded credential — it is not one. Treating
  // it as usable would mean attempting `PSW?undefined` against a real controller, which is
  // exactly the "cycled guess" D84 prohibits, arrived at by accident.
  if (bleName === undefined || blePassword === undefined || expiresAt === undefined) {
    return { ok: false, status: response.status, code: 'malformed-ble-access' }
  }

  const serviceUuid = stringField(response.body, 'bleServiceUuid')
  const charUuid = stringField(response.body, 'bleCharUuid')
  const serial = stringField(response.body, 'bleSerial')
  return ok(response.status, {
    gateId,
    bleName,
    blePassword,
    expiresAt,
    ...(serviceUuid === undefined ? {} : { bleServiceUuid: serviceUuid }),
    ...(charUuid === undefined ? {} : { bleCharUuid: charUuid }),
    ...(serial === undefined ? {} : { bleSerial: serial }),
  })
}

/** What the server acknowledged. One shape for reconciled, duplicate, and D69's discard alike. */
export interface ReceiptAck {
  readonly reconciliationId: string
  /** The **stored** instant — a duplicate learns the first upload's, per §9.2. */
  readonly occurredAt: string
}

/**
 * The body of one queued receipt, exactly as the server's schema declares it.
 *
 * **It is not the stored row.** `outbox.ts` adds the fields a *queue* needs — an account
 * partition, an attempt count — and this is the subset that crosses the wire. Keeping them apart
 * is what stops a bookkeeping field from drifting onto an append-only audit row.
 */
export interface ReceiptUpload {
  readonly reconciliationId: string
  readonly occurredAt: string
  readonly result: 'success' | 'failure'
  readonly failureReason?: 'auth-failed' | 'timeout' | 'disconnected' | 'unavailable'
  readonly latencyMs?: number
}

export async function uploadReceipt(
  request: ApiRequestFn,
  gateId: string,
  body: ReceiptUpload,
): Promise<ApiResult<ReceiptAck>> {
  const response = await request({
    method: ROUTES.gateReceipts.method,
    path: buildPath(ROUTES.gateReceipts, { gateId }),
    body,
  })
  if (!isSuccess(response.status)) return fail(response)

  // `field` rather than `stringField` for the echo, so a server that answered with a shape this
  // client does not recognize still counts as an acknowledgement: the receipt reached the
  // server, which is the only fact the caller needs before deleting it.
  const reconciliationId = stringField(response.body, 'reconciliationId') ?? body.reconciliationId
  const occurredAt = stringField(response.body, 'occurredAt') ?? body.occurredAt
  void field(response.body, 'status')
  return ok(response.status, { reconciliationId, occurredAt })
}
